import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Brain, Zap, FileText, Sparkles, TrendingUp, ArrowRight } from 'lucide-react';
interface AIToolsPageProps {
  onNavigate?: (path: string) => void;
}
export function AIToolsPage({
  onNavigate
}: AIToolsPageProps) {
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933]" data-id="element-566">
      <Navbar onNavigate={onNavigate} data-id="element-567" />

      <section className="py-24" data-id="element-568">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-569">
          <div className="text-center mb-16" data-id="element-570">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-571">
              AI Tools & Infrastructure
            </h1>
            <p className="text-lg font-mono text-[#9ca3af] max-w-2xl mx-auto" data-id="element-572">
              Leverage AI to amplify your thinking, not replace your voice
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-573">
            <div className="bg-white border-2 border-dashed border-salon-teal p-8" data-id="element-574">
              <Brain className="w-12 h-12 text-salon-teal mb-4" data-id="element-575" />
              <h3 className="text-xl font-bold mb-4" data-id="element-576">Your Content Machine</h3>
              <p className="text-[#1f2933]/80 mb-6" data-id="element-577">
                Learn to build a custom AI-powered content machine that
                generates ideas, creates drafts, and maintains your authentic
                voice across all platforms.
              </p>
              <ul className="space-y-2 text-sm" data-id="element-578">
                <li className="flex items-start gap-2" data-id="element-579">
                  <span className="text-salon-teal" data-id="element-580">•</span>
                  <span data-id="element-581">Custom prompt templates for your niche</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-582">
                  <span className="text-salon-teal" data-id="element-583">•</span>
                  <span data-id="element-584">Voice preservation techniques</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-585">
                  <span className="text-salon-teal" data-id="element-586">•</span>
                  <span data-id="element-587">Quality control workflows</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-588">
                  <span className="text-salon-teal" data-id="element-589">•</span>
                  <span data-id="element-590">Batch content creation systems</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border-2 border-dashed border-salon-yellow p-8" data-id="element-591">
              <Zap className="w-12 h-12 text-salon-yellow mb-4" data-id="element-592" />
              <h3 className="text-xl font-bold mb-4" data-id="element-593">Idea Generation Engine</h3>
              <p className="text-[#1f2933]/80 mb-6" data-id="element-594">
                Never run out of content ideas again. Build systems that
                generate 10-20 ideas per week aligned with your content
                framework.
              </p>
              <ul className="space-y-2 text-sm" data-id="element-595">
                <li className="flex items-start gap-2" data-id="element-596">
                  <span className="text-salon-yellow" data-id="element-597">•</span>
                  <span data-id="element-598">Trending topic monitoring</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-599">
                  <span className="text-salon-yellow" data-id="element-600">•</span>
                  <span data-id="element-601">Audience question mining</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-602">
                  <span className="text-salon-yellow" data-id="element-603">•</span>
                  <span data-id="element-604">Content angle multiplication</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-605">
                  <span className="text-salon-yellow" data-id="element-606">•</span>
                  <span data-id="element-607">Evergreen content identification</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white border-2 border-dashed border-[#9ca3af] p-12 mb-16" data-id="element-608">
            <h2 className="text-2xl font-bold mb-8 text-center" data-id="element-609">
              The AI Workflow
            </h2>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6" data-id="element-610">
              <div className="text-center" data-id="element-611">
                <div className="w-16 h-16 bg-salon-red/10 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-612">
                  <FileText className="w-8 h-8 text-salon-red" data-id="element-613" />
                </div>
                <h3 className="font-bold mb-2" data-id="element-614">Raw Input</h3>
                <p className="text-sm text-[#9ca3af] font-mono" data-id="element-615">
                  Your expertise & ideas
                </p>
              </div>
              <ArrowRight className="w-6 h-6 text-[#9ca3af] rotate-90 md:rotate-0" data-id="element-616" />
              <div className="text-center" data-id="element-617">
                <div className="w-16 h-16 bg-salon-teal/10 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-618">
                  <Brain className="w-8 h-8 text-salon-teal" data-id="element-619" />
                </div>
                <h3 className="font-bold mb-2" data-id="element-620">AI Processing</h3>
                <p className="text-sm text-[#9ca3af] font-mono" data-id="element-621">
                  Expansion & structuring
                </p>
              </div>
              <ArrowRight className="w-6 h-6 text-[#9ca3af] rotate-90 md:rotate-0" data-id="element-622" />
              <div className="text-center" data-id="element-623">
                <div className="w-16 h-16 bg-salon-yellow/10 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-624">
                  <Sparkles className="w-8 h-8 text-salon-yellow" data-id="element-625" />
                </div>
                <h3 className="font-bold mb-2" data-id="element-626">Human Refinement</h3>
                <p className="text-sm text-[#9ca3af] font-mono" data-id="element-627">
                  Your voice & polish
                </p>
              </div>
              <ArrowRight className="w-6 h-6 text-[#9ca3af] rotate-90 md:rotate-0" data-id="element-628" />
              <div className="text-center" data-id="element-629">
                <div className="w-16 h-16 bg-salon-red rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-630">
                  <TrendingUp className="w-8 h-8 text-white" data-id="element-631" />
                </div>
                <h3 className="font-bold mb-2" data-id="element-632">Authority Asset</h3>
                <p className="text-sm text-[#9ca3af] font-mono" data-id="element-633">
                  Published content
                </p>
              </div>
            </div>
          </div>

          <div className="text-center bg-[#1f2933] text-white p-12" data-id="element-634">
            <h2 className="text-2xl font-bold mb-4" data-id="element-635">
              Master AI-Powered Content Creation
            </h2>
            <p className="max-w-2xl mx-auto mb-8 text-[#f5f5f7]/80" data-id="element-636">
              Learn to build your own content machine that reduces creation time
              by 80% while maintaining quality and authenticity.
            </p>
            <Button size="large" onClick={() => onNavigate && onNavigate('/initiate-access')} className="bg-salon-red hover:bg-salon-red/90 border-salon-red" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-638" />} data-id="element-637">
              GET_STARTED
            </Button>
          </div>
        </div>
      </section>

      <Footer onNavigate={onNavigate} data-id="element-639" />
    </div>;
}